import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import Section, { SectionHeading } from "@/components/ui/Section";
import { ArrowLink } from "@/components/ui/CTAButton";
import Reveal from "@/components/motion/Reveal";

// Envoltorio reutilizable que muestra sus `children` (tarjetas) en un carrusel
// horizontal con scroll-snap nativo. Base común de los carruseles de Podcast,
// Noticias y Eventos:
//   • Deslizamiento táctil (scroll-snap nativo) y con botones de flecha.
//   • Navegación por páginas con teclado (← →) cuando la pista tiene el foco.
//   • Indicadores de posición bajo el carrusel.
//   • Flechas deshabilitadas en la primera/última página.
//   • `maxItems` opcional para limitar las tarjetas cargadas.
// Los controles solo aparecen cuando el contenido desborda.
export default function CarouselSection({
  title,
  label,
  description,
  linkText,
  linkTo,
  tone = "light",
  children,
  testId,
  maxItems,
  // "card": el de siempre, varias tarjetas de ancho fijo a la vista.
  // "full": una tarjeta por página, a todo el ancho de la sección — pedido
  // para "Qué Hacemos". Las demás secciones (Eventos, Noticias, Podcast) no
  // pasan esta prop y no cambian.
  slideWidth = "card",
}) {
  const trackRef = useRef(null);
  const [pageCount, setPageCount] = useState(1);
  const [activePage, setActivePage] = useState(0);

  const items = React.Children.toArray(children);
  const limited = Number.isFinite(maxItems) ? items.slice(0, maxItems) : items;

  // Recalcula el número de páginas (según cuántas tarjetas caben a la vista) y
  // cuál está activa. Se invoca al hacer scroll y al redimensionar.
  //
  // En modo "full" no se estima por cociente de anchos: con una tarjeta al
  // 100 % del ancho más el `gap` entre tarjetas, `scrollWidth` siempre supera
  // a `pageCount * clientWidth` en la suma de los huecos, y
  // `Math.ceil(scrollWidth / clientWidth)` cuenta una página fantasma de más.
  // Como aquí una página es siempre una tarjeta, se cuenta directamente.
  const recalc = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    const pages =
      slideWidth === "full"
        ? Math.max(1, limited.length)
        : clientWidth > 0
        ? Math.max(1, Math.ceil(scrollWidth / clientWidth))
        : 1;
    setPageCount(pages);
    // En el extremo derecho el navegador limita el scroll, así que el final se
    // detecta explícitamente para que el último indicador refleje la posición.
    const atEndNow = scrollLeft + clientWidth >= scrollWidth - 1;
    const page = atEndNow ? pages - 1 : Math.round(scrollLeft / clientWidth);
    setActivePage(Math.min(pages - 1, Math.max(0, page)));
  }, [slideWidth, limited.length]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    el.addEventListener("scroll", recalc, { passive: true });
    window.addEventListener("resize", recalc);
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", recalc);
      window.removeEventListener("resize", recalc);
    };
  }, [recalc, limited.length]);

  const showNav = pageCount > 1;
  const atStart = activePage <= 0;
  const atEnd = activePage >= pageCount - 1;

  const goToPage = page => {
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.min(pageCount - 1, Math.max(0, page));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };

  const onKeyDown = e => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      goToPage(activePage + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      goToPage(activePage - 1);
    }
  };

  return (
    <Section tone={tone} data-testid={testId}>
      {/* Cabecera: título a la izquierda, enlace «ver todos» a la derecha */}
      <Reveal>
        <div className="flex items-end justify-between gap-6 mb-12">
          <SectionHeading label={label} title={title} description={description} className="mb-0" />
          {linkText && linkTo && (
            <div className="hidden sm:flex items-center flex-shrink-0">
              <ArrowLink to={linkTo}>
                {linkText} <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </ArrowLink>
            </div>
          )}
        </div>
      </Reveal>

      {/* Pista del carrusel + botones de navegación */}
      <div className="relative">
        <button
          type="button"
          aria-label="Tarjetas anteriores"
          hidden={!showNav}
          disabled={atStart}
          className="carousel-btn prev focus-ring"
          onClick={() => goToPage(activePage - 1)}
        >
          <ChevronLeft className="w-5 h-5" aria-hidden="true" />
        </button>

        <div
          className={`carousel-track${slideWidth === "full" ? " carousel-track--full" : ""}`}
          ref={trackRef}
          role="region"
          aria-roledescription="carrusel"
          aria-label={title || label || "Carrusel"}
          tabIndex={0}
          onKeyDown={onKeyDown}
        >
          {limited.map((child, i) => (
            // Las tarjetas entran escalonadas, no todas de golpe.
            <Reveal key={child.key ?? i} delay={Math.min(i, 4) * 70} distance={24}>
              {child}
            </Reveal>
          ))}
        </div>

        <button
          type="button"
          aria-label="Tarjetas siguientes"
          hidden={!showNav}
          disabled={atEnd}
          className="carousel-btn next focus-ring"
          onClick={() => goToPage(activePage + 1)}
        >
          <ChevronRight className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>

      {/* Indicadores de posición.
          Antes eran role="tab" sin ningún tabpanel asociado, un uso incorrecto
          del patrón de pestañas que confunde a los lectores de pantalla. Son
          botones normales dentro de un grupo con nombre, y el estado se anuncia
          con aria-current. */}
      {showNav && (
        <div className="carousel-dots" role="group" aria-label="Posición del carrusel">
          {Array.from({ length: pageCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Ir a la página ${i + 1} de ${pageCount}`}
              aria-current={i === activePage ? "true" : undefined}
              className={`carousel-dot ${i === activePage ? "active" : ""}`}
              onClick={() => goToPage(i)}
            />
          ))}
        </div>
      )}
    </Section>
  );
}
